import { Feature } from "geojson";
import { pick } from "lodash";
import { MapMouseEvent, PointLike } from "mapbox-gl";
import { useEffect } from "react";

import { parseStringifiedProperties } from "../parseStringifiedProperties";

type UseDragInitiationProps<T extends Feature = Feature> = {
    map: mapboxgl.Map | null;
    layerIds: string[];
    isDraggable: (feature: T) => boolean;
    hoverBoundingBoxSize: number;
    onDragStart: (feature: T, position: [number, number]) => void;
};

/**
 * Handles mousedown events to initiate dragging
 */
export function useDragInitiation<T extends Feature = Feature>({
    map,
    layerIds,
    isDraggable,
    hoverBoundingBoxSize,
    onDragStart,
}: UseDragInitiationProps<T>) {
    useEffect(() => {
        if (!map) return;

        function handleMouseDown(e: MapMouseEvent) {
            if (!map || !e.point) return;

            const bbox: [PointLike, PointLike] = [
                [e.point.x - hoverBoundingBoxSize, e.point.y - hoverBoundingBoxSize],
                [e.point.x + hoverBoundingBoxSize, e.point.y + hoverBoundingBoxSize],
            ];

            const [feature] = map.queryRenderedFeatures(bbox, {
                layers: layerIds,
            });

            if (!feature) return;

            const pickedFeature = pick(feature, ["id", "type", "bbox", "geometry", "properties"]) as T;

            if (pickedFeature.properties) {
                pickedFeature.properties = parseStringifiedProperties(pickedFeature.properties);
            }

            // Check if feature is draggable
            if (!isDraggable(pickedFeature)) {
                return;
            }

            // Prevent default map drag
            e.preventDefault();
            const startGeoPos = [e.lngLat.lng, e.lngLat.lat] as [number, number];

            onDragStart(pickedFeature, startGeoPos);
        }

        layerIds.forEach(layerId => {
            map.on("mousedown", layerId, handleMouseDown);
        });

        return () => {
            if (!map) return;
            layerIds.forEach(layerId => {
                map.off("mousedown", layerId, handleMouseDown);
            });
        };
    }, [map, layerIds, isDraggable, hoverBoundingBoxSize, onDragStart]);
}
